import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCBadge,
	RCCTooltip
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AppIcons from "src/components/AppIcons";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import LovIndicatorFieldsOnLogs from "src/components/Lov/IndicatorFieldsOnLogs";
import PageTitle from "src/components/PageTitle";
import Pagination from "src/components/Pagination";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import formatDate from "src/utils/formatDate";
import { css } from "styled-components";
import { ILogListFilters, IRefLogErrorsModal } from "./interface";
import ImportLogsErrorsModal from "./ModalLogErrors";

const ValuesImportLogs: React.FC = () => { 

	const paginationRef = useRef<IForwardPagination>(null);

	const { translate,currentLanguage } = useTranslation();
	const [filters,setFilters] = useState<ILogListFilters>();
	const [showFilters,setShowFilters] = useState<boolean>(false);
	const modalErrors = useRef<IRefLogErrorsModal>(null);

	const {
		control:controlFilters,
		setValue:setValueFilters,
		reset:resetFilters,
		handleSubmit:handleSubmitFilters
	}= useForm();
    
	const submitFilters = async(data:any) => {
		setFilters(() => {
			const newState = {} as ILogListFilters;
			if(data.origin)
				newState.origin = typeof data.origin === "object" ? data.origin?.value : data.origin;
			if(data.dateInit)
				newState.dateInit = formatDate(data.dateInit);
			if(data.dateEnd)
				newState.dateEnd = formatDate(data.dateEnd);
			if(data.logFields)
				newState.fields = data.logFields.map((x:any) => x.value).join(",");

			return newState;
		});
		paginationRef.current?.reload();
	};

	const useQuery=(param:string) => {
		const query = new URLSearchParams(useLocation().search);
		return query.get(param)??"";
	};

	const colStatus = (row: any) => { 
		
		let statusColor:"green"|"yellow"|"red";
		let statusText;
		switch (row.iql001Resultados) {
		case 1:
			statusColor = "green";
			statusText="Sucesso";
			break;
		case 2:
			statusColor = "red";
			statusText="Falha";
			break;
		default:
			statusColor = "yellow";
			statusText="Parcial";
			break;
		}

		return(	
			<RCCBadge 
				color={statusColor}
				styleInline="font-size: 0.83rem;"
			>
				{translate(statusText)}
			</RCCBadge>
		);
	};

	const colButton= (row:any) => (
		<RCCTooltip
			text={translate("Mensagens de importação")}
			position="left"
		>
			<RCCButton
				icon={{name: "stack", color: "grey", size: 18}}
				color="transparent"
				size="md"

				onClick={() => {modalErrors.current?.actionButtonOpen(row.iql001Id);}}
			/>
		</RCCTooltip>
	);

	return (
		<>
			<RCCGrid
				container
				item
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Logs de importação")}
					icon={() => <AppIcons name="indicators" />}
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={
							showFilters || 
							filters?.dateEnd || 
							filters?.dateInit || 
							filters?.origin ||
							filters?.fields ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								showFilters || 
								filters?.dateEnd || 
								filters?.dateInit || 
								filters?.origin ||
								filters?.fields ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={
						() => resetFilters({
							origin: {value:"",label:""},
							dateInit:"",
							dateEnd:"",
							fields:""
						})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
							>
								<Select 
									control={controlFilters}
									name="origin" 
									options={[
										{value: "0", label: translate("Todos")}, 
										{value: "1", label: translate("Manual")},
										{value: "2", label: translate("Integração")}
									]} 
									label={translate("Situação")}
									defaultValue={useQuery("origin")}
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
							>
								<LovIndicatorFieldsOnLogs
									control={controlFilters}
									name="logFields"
									label={translate("Campos")}
									isClearable
									isMulti
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={6}
								md={4}
							>
								<DateTimePickerRange
									control={controlFilters}
									nameInit="dateInit"
									nameEnd="dateEnd"
									label={translate("Período")}
									setValue={setValueFilters}
									type="datePicker"
								/> 
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="IndicatorsValues/ListLogs"
					filtersData={filters}    
					styleInline={{row: css`
					>div{
						>div{@media (max-width: 600px) {
								width:100%;
								max-width: 100%;
							}
						}
					}
				`}}
					columns={[
						{
							idColumn: "nomeImportação",
							nameColumn: translate("Origem"),
							xs:1,
							sm: 4,
							type: "string"
						},
						{
							idColumn: "iql001Dthrinicio",
							nameColumn: translate("Início"),
							xs:1,
							sm: 2,
							type: "date",
							format:{date:currentLanguage === "pt" ? "dd/MM/yyyy HH:mm":"MM/dd/yyyy HH:mm"}
						},
						{
							idColumn: "iql001Dthrfim",
							nameColumn: translate("Fim"),
							xs:1,
							sm: 2,
							type: "date",
							format:{date:currentLanguage === "pt" ? "dd/MM/yyyy HH:mm":"MM/dd/yyyy HH:mm"}
						},
						{
							idColumn: "iql001Regprocessos",
							nameColumn: translate("Processados"),
							xs:1,
							sm: 1,
							type: "string"
						},
						{
							idColumn: "iql001Regatualizados",
							nameColumn: translate("Atualizados"),
							xs:1,
							sm: 1,
							type: "string"
						},
						{
							idColumn: "iql001Resultados",
							nameColumn: translate("Resultado"),
							xs:1,
							sm: 1,
							cellRenderer:colStatus
						},
						{
							idColumn: "btm",
							nameColumn:" ",
							xs:1,
							sm: 1,
							cellRenderer:colButton
						}
					]}
				/>
				<ImportLogsErrorsModal
					ref={modalErrors}
				/>
			</RCCGrid>
		</>
	);
};

export default ValuesImportLogs;
